import { useEffect, useState } from 'react';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { ApiError } from '../../../../../shared/shared.api';
import { toVerificationInput, type VerificationFormValues } from '../policies/verification-form.policy';
import { workOrderNumber } from '../policies/work-order-labels';
import { listVerifications, recordVerification, type WorkOrderVerificationView } from '../work-orders.api';

export type VerificationsState =
  { status: 'loading' } | { status: 'loaded'; attempts: WorkOrderVerificationView[] } | { status: 'forbidden' } | { status: 'error' };

/** Past attempts, and recording a new one. The outcome comes back from the server, derived from the checklist, and the toast says what it was; `onRecorded` lets the page re-read the order, which has moved. */
export function useVerifications(order: { id: string; sequenceNumber: number }, onRecorded: () => Promise<void>) {
  const [state, setState] = useState<VerificationsState>({ status: 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    let cancelled = false;
    listVerifications(order.id)
      .then((attempts) => !cancelled && setState({ status: 'loaded', attempts }))
      .catch((err) => !cancelled && setState({ status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' }));
    return () => {
      cancelled = true;
    };
  }, [order.id]);

  async function submit(values: VerificationFormValues): Promise<boolean> {
    setError(null);
    setSubmitting(true);
    try {
      const { verification } = await recordVerification(order.id, toVerificationInput(values));
      setState((current) => ({ status: 'loaded', attempts: [...(current.status === 'loaded' ? current.attempts : []), verification] }));
      const number = workOrderNumber(order.sequenceNumber);
      toast.success(verification.outcome === 'passed' ? `Work order ${number} verified and closed` : `Work order ${number} sent back to in progress`);
      await onRecorded();
      return true;
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not record the verification — try again.');
      return false;
    } finally {
      setSubmitting(false);
    }
  }

  return { state, error, submitting, submit };
}
